import React, { useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { fetchAPI } from '../../../api/request';
import Alert from '../../../components/common/Alert.component';
import Back from '../../../components/common/Back.component';
import Loading from '../../../components/common/Loading.component';
import { getViewConfiguration, Roles, UsersType } from '../../../module/contacts/conf/Data.conf';
import dayjs from 'dayjs';
import EditView from '../../../views/Edit.view';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

const ContactsEditPage = ({ module: { edit, query, tag } }) => {
  const { jwt } = useSelector((state) => state.login);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [userTypeSelect, setUserTypeSelect] = useState(null);
  const [crewResponsability, setcrewResponsability] = useState(null);

  const { loading: loadingGet, data } = useSelector(edit.selector.data);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const onError = useCallback((error) => {
    setSuccess(false);
    setLoading(false);
    setError(error);
  }, []);

  const onSuccess = useCallback(() => {
    setSuccess(true);
    setError(false);
    setLoading(false);
  }, []);

  const handleChangeCrewResponsability = useCallback((newValue) => {
    setcrewResponsability(newValue);
  }, []);

  const handleUpdateUserNa = useCallback(
    (values) => {
      const obj = {
        id_department: values?.id_department,
        subrole: values?.subrole,
      };

      fetchAPI(
        {
          path: `/users-nautica-departments/${data?.id_users_nautica?.id_users_nautica_departments?.[0]?.id}`,
          method: 'PUT',
        },
        {},
        '',
        { data: { ...obj } },
        jwt
      )
        .then(() => {
          onSuccess();
        })
        .catch(onError);
    },
    [jwt, onError, onSuccess, data]
  );

  useEffect(() => {
    if (!data) return;

    const isNautica =
      location.pathname.includes('/na/') ||
      !!data?.id_users_nautica ||
      data?.userType?.label === 'Nautica';

    if (isNautica) {
      if (data?.userType?.label) {
        setUserTypeSelect({
          id: data.userType.label,
          label: data.userType.label,
        });
      } else {
        setUserTypeSelect({
          id: 'Nautica',
          label: 'Nautica',
        });
      }

      if (data?.crew_responsability?.label) {
        setcrewResponsability(data.crew_responsability);
      }
      return;
    }

    setUserTypeSelect({
      id: 'Crew',
      label: 'Crew',
    });

    if (data?.crew_responsability?.label) {
      const selectedRole = Roles.find(
        (role) => role.label === data.crew_responsability.label
      );
      setcrewResponsability(selectedRole || data.crew_responsability);
    } else if (data?.crew_responsability) {
      const selectedRole = Roles.find(
        (role) => role.label === data.crew_responsability
      );
      setcrewResponsability(
        selectedRole || {
          id: data.crew_responsability,
          label: data.crew_responsability,
        }
      );
    }
  }, [data, location.pathname]);

  const onSubmit = useCallback(
    (values) => {
      setSuccess(false);
      setError(false);
      setLoading(true);

      if (userTypeSelect?.id === 'Nautica') {
        const obj = {
          username: values?.username,
          email: values?.email,
          surname: values?.surname,
          name: values?.name,
          id_users_role: values?.id_users_role,
          phone_number: values?.phone_number,
          extension_number: values?.extension_number,
          id_site: values?.id_site,
          blocked: values?.blocked,
          id_teams: values?.id_teams?.map((t) => t.id) ?? [],
        };

        const nautica_id = data?.id_users_nautica?.id;

        if (values?.password?.trim() === '') {
          delete obj.password;
        } else if (values?.password) {
          obj.password = values.password;
        }

        fetchAPI(
          { path: `/users-nauticas/${nautica_id}`, method: 'PUT' },
          {},
          '',
          { data: obj },
          jwt
        )
          .then((resp) => {
            handleUpdateUserNa({
              id_users_nautica: resp.data.id,
              ...values,
            });
            onSuccess();
          })
          .catch(onError);

        return;
      }

      const obj = {
        username: values?.username,
        email: values?.email,
        name: values?.name,
        surname: values?.surname,
        phone_number: values?.country_code
          ? `${values?.country_code?.phone}-${values?.phone_number}`
          : values?.phone_number,
        birthdate: values?.birthdate
          ? dayjs(values?.birthdate).format('YYYY-MM-DD')
          : null,
        start_date: values?.start_date
          ? dayjs(values?.start_date).format('YYYY-MM-DD')
          : null,
        end_date: values?.end_date
          ? dayjs(values?.end_date).format('YYYY-MM-DD')
          : null,
        crew_responsability: values?.crew_responsability?.label,
        is_delivery_manager_on_board: values?.is_delivery_manager_on_board,
        is_main_contact: values?.is_main_contact,
      };

      const user_id = data?.id;

      fetchAPI(
        { path: `/user-yacht-members/${user_id}`, method: 'PUT' },
        {},
        '',
        { data: obj },
        jwt
      )
        .then(() => {
          onSuccess();
          setLoading(false);
        })
        .catch(onError);
    },
    [
      userTypeSelect?.id,
      data?.id,
      data?.id_users_nautica?.id,
      handleUpdateUserNa,
      jwt,
      onError,
      onSuccess,
    ]
  );

  useEffect(() => {
    dispatch(
      query.detail.action({
        settings: query.detail.settings,
        token: jwt,
        params: { id },
        query: { populate: query.detail.populate },
      })
    );

    return () => {
      dispatch(
        query.detail.action({
          settings: null,
          token: null,
          params: null,
          query: null,
        })
      );
    };
  }, [id, dispatch, jwt, query]);

  const editViewConf = getViewConfiguration(
    userTypeSelect,
    crewResponsability,
    edit.conf
  );

  return (
    <Container disableGutters maxWidth={false} sx={{ height: '100%' }}>
      <Stack spacing={2} justifyContent='center' alignItems='center' width={1}>
        <Back title={tag} label={edit?.back?.label} />
        {(loading || loadingGet) && <Loading />}
        {success && <Alert severity='success'>Contact updated successfully</Alert>}
        {error && (
          <Alert severity="error">
            {error?.response?.data?.error?.message || 'An error has occurred'}
          </Alert>
        )}
        <Paper variant='outlined' sx={{ padding: 2, width: '100%' }}>
          <Box sx={{ padding: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'none' }}>
                <Autocomplete
                  disablePortal
                  options={UsersType}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  getOptionLabel={(option) => option?.label || ''}
                  value={userTypeSelect}
                  disabled
                  renderInput={(params) => (
                    <TextField {...params} label='User Type' />
                  )}
                />
              </Grid>

              {userTypeSelect?.id === 'Crew' && (
                <Grid item xs={12}>
                  <Autocomplete
                    disablePortal
                    options={Roles}
                    disabled
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.label
                    }
                    onChange={(_, newValue) => {
                      handleChangeCrewResponsability(newValue);
                    }}
                    getOptionLabel={(option) => option?.label || ''}
                    value={crewResponsability}
                    renderInput={(params) => (
                      <TextField {...params} label='Crew Responsability' />
                    )}
                  />
                </Grid>
              )}
            </Grid>

            {editViewConf && (
              <EditView
                data={data}
                id={id}
                conf={editViewConf}
                disabled={loading}
                onEditSubmit={onSubmit}
                onEditError={onError}
                onEditSuccess={onSuccess}
              />
            )}
          </Box>
        </Paper>
      </Stack>
    </Container>
  );
};

export default ContactsEditPage;